import { cn } from "@wellcheck/components/lib/cn";

import type { ScaleChoice } from "@/features/respond/data/types";

interface ScaleFieldProps {
  name: string;
  label: string;
  scale: ReadonlyArray<ScaleChoice>;
  value: number | undefined;
  onChange: (value: number) => void;
  required?: boolean;
}

/**
 * ScaleField — 4 段階ラジオを横並びで提供する基本フィールド。
 *
 * BJSQ 紙票のフォーマットに寄せて「質問文 + 1〜4 のラジオ」を 1 行で表現する。
 * sm 以上では grid 4 列で固定し、選択肢が折り返さないようにする。
 * モバイルでは 2 列に折り返す。
 */
export function ScaleField({ name, label, scale, value, onChange, required }: ScaleFieldProps) {
  const answered = value !== undefined;
  return (
    <fieldset className="space-y-3 border-t border-border pt-5 first:border-t-0 first:pt-0">
      <legend className="flex items-start gap-2 text-lg leading-relaxed">
        <span
          aria-hidden
          className={cn(
            "mt-1 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-xs transition-colors",
            answered
              ? "bg-primary text-primary-foreground"
              : "border border-border bg-background text-transparent",
          )}
        >
          ✓
        </span>
        <span>
          {label}
          {required && <span className="ml-1 text-destructive">*</span>}
        </span>
      </legend>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {scale.map((choice) => {
          const checked = value === choice.value;
          return (
            <label
              key={choice.value}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-base transition-colors",
                checked
                  ? "border-primary bg-primary/10 font-medium text-foreground"
                  : "border-border bg-background text-muted-foreground hover:bg-muted/50",
              )}
            >
              <input
                type="radio"
                name={name}
                value={choice.value}
                checked={checked}
                onChange={() => onChange(choice.value)}
                className="size-5 shrink-0 accent-primary"
              />
              <span className="min-w-0 leading-snug">
                <span className="mr-1 tabular-nums">{choice.value}.</span>
                {choice.label}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
